import { ProfileDocuments } from './ProfileDocuments';
import { codeLabel } from '../../utils/codeLabel';
import { ErrorState, LoadingState, StaleBanner } from '../ui/AsyncState';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoveryQuery as useQuery } from '../ui/useRecoveryQuery';
import { Link } from 'react-router-dom';
import { getUserById } from '../../api/endpoints/admin';
import type {
  KycStatus,
  DriverProfileResponse,
  ShipperProfileResponse,
  FleetManagerProfileResponse,
} from '../../api/types';
import { StatusPill } from '../ui/StatusPill';
import { userDisplayName } from './partyLabel';
import { kycStatusColor } from './statusColor';
import { UserFormModal } from './UserFormModal';
import { AccountManagerSection } from '../../features/ownership/AccountManagerSection';
import { tons } from '../../utils/weight';
import styles from './UserDetailDrawer.module.css';
import { staticQueryPolicy } from '../../api/queryPolicies';
import { ShipperPaymentTerms } from '../../features/payments/ShipperPaymentTerms';
import { DialogHelp } from '../../help/DialogHelp';

interface Props {
  userId: number;
  onClose: () => void;
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * User profile drawer (basic info + KYC review + sub-profile) — shared by
 * the Drivers/Shippers/Fleet managers views and the shipment Overview's
 * shipper row, so the profile opens in place over whatever screen you're on.
 */
export function UserDetailDrawer({ userId, onClose }: Props) {
  const { t } = useTranslation();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const {
    data: detailData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'users', 'detail', userId],
    queryFn: () => getUserById(userId),
    ...staticQueryPolicy,
    // #607: other screens fill this key and the list re-fetches on every search,
    // so a cached copy can be older than the row that was clicked. Show it at once,
    // but always re-read on open so drawer, edit modal and list agree.
    refetchOnMount: 'always',
  });

  const role = detailData?.user.role;
  const profile = detailData?.profile as
    DriverProfileResponse | ShipperProfileResponse | FleetManagerProfileResponse | null;
  const name = detailData ? userDisplayName(detailData.user) : null;

  return (
    <>
      <div className={styles.drawerOverlay} onClick={onClose}>
        <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
          <div className={styles.drawerHeader} data-testid="drawer-header">
            <div className={styles.titleBlock}>
              <h2 className={styles.drawerTitle}>{name ? name.headline : 'User profile'}</h2>
              {name?.secondary && <div className={styles.drawerSubtitle}>{name.secondary}</div>}
            </div>
            <div className={styles.headerActions}>
              <DialogHelp context="user-drawer" title={name ? name.headline : 'User profile'} />
              {detailData && detailData.user.role !== 'ADMIN' && (
                <button
                  className={styles.editBtn}
                  // Seed the edit modal from the re-read, never from an older cached copy (#607).
                  disabled={isFetching}
                  onClick={() => setIsEditOpen(true)}
                >
                  {t('domain.userDetailDrawer.edit')}
                </button>
              )}
              <button className={styles.closeBtn} onClick={onClose}>
                ✕
              </button>
            </div>
          </div>

          <div className={styles.drawerContent}>
            {isError && detailData && <StaleBanner onRetry={() => refetch()} />}
            {isLoading ? (
              <LoadingState label={t('domain.userDetailDrawer.loading')} />
            ) : !detailData ? (
              <ErrorState title={t('domain.userDetailDrawer.loadError')} onRetry={() => refetch()} />
            ) : (
              <div className={styles.detailsList}>
                <div className={styles.sectionTitle}>{t('domain.userDetailDrawer.basicInfo')}</div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelUserId')}</span>
                  <span className={`${styles.detailVal} ${styles.mono}`}>
                    #{detailData.user.id}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelCognitoSub')}</span>
                  <span className={`${styles.detailVal} ${styles.mono}`}>
                    {detailData.user.cognitoSub}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelFullName')}</span>
                  <span className={styles.detailVal}>{detailData.user.fullName || '—'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelPhoneNumber')}</span>
                  <span className={styles.detailVal}>{detailData.user.phoneNumber}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelEmailAddress')}</span>
                  <span className={styles.detailVal}>{detailData.user.email || '—'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelRole')}</span>
                  <span className={styles.detailVal}>{detailData.user.role}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelKycStatus')}</span>
                  <span className={styles.detailVal}>
                    <StatusPill
                      bg={kycStatusColor(detailData.user.kycStatus as KycStatus).bg}
                      text={kycStatusColor(detailData.user.kycStatus as KycStatus).text}
                      label={codeLabel('kyc', detailData.user.kycStatus)}
                    />
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelSystemStatus')}</span>
                  <span className={styles.detailVal}>{codeLabel('userStatus', detailData.user.status)}</span>
                </div>

                {role === 'SHIPPER' && <AccountManagerSection shipperId={userId} />}
                {role === 'SHIPPER' && (
                  <ShipperPaymentTerms
                    key={userId}
                    userId={userId}
                    gstin={(profile as ShipperProfileResponse | null)?.gstin}
                    paymentTermsDays={(profile as ShipperProfileResponse | null)?.paymentTermsDays}
                    gstMode={(profile as ShipperProfileResponse | null)?.gstMode}
                    gstRatePct={(profile as ShipperProfileResponse | null)?.gstRatePct}
                  />
                )}

                <ProfileDocuments
                  key={userId}
                  userId={userId}
                  profile={profile}
                  avatarUrl={detailData.user.avatarUrl}
                />

                {detailData.user.role !== 'ADMIN' && (
                  <Link
                    to={`/users/${userId}?tab=kyc`}
                    state={{ from: window.location.pathname }}
                    className={styles.fullProfileBtn}
                    onClick={onClose}
                  >
                    {t('domain.userDetailDrawer.reviewKyc')}
                  </Link>
                )}

                <div className={styles.sectionTitle} style={{ marginTop: '20px' }}>
                  {t('domain.userDetailDrawer.subProfileDetails', { role: detailData.user.role.toLowerCase().replace('_', ' ') })}
                </div>
                {profile ? (
                  <div className={styles.subProfileDetails}>
                    {role === 'DRIVER' && (
                      <>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelLicenseNumber')}</span>
                          <span className={styles.detailVal}>
                            {(profile as DriverProfileResponse).licenseNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelLicenseExpiry')}</span>
                          <span className={styles.detailVal}>
                            {fmtDate((profile as DriverProfileResponse).licenseExpiry)}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelAvailability')}</span>
                          <span className={styles.detailVal}>
                            {(profile as DriverProfileResponse).isAvailable
                              ? 'Available'
                              : 'Not Available'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelCurrentVehicle')}</span>
                          <span className={styles.detailVal}>
                            {(profile as DriverProfileResponse).vehicleNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelCapacity')}</span>
                          <span className={styles.detailVal}>
                            {tons((profile as DriverProfileResponse).vehicleCapacityKg)}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelDrivingLicense')}</span>
                          <span className={styles.detailVal}>
                            {(profile as DriverProfileResponse).dlDocumentUrl ? (
                              <span>{t('domain.userDetailDrawer.attached')}</span>
                            ) : (
                              '—'
                            )}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelVehicleRc')}</span>
                          <span className={styles.detailVal}>
                            {(profile as DriverProfileResponse).rcDocumentUrl ? (
                              <span>{t('domain.userDetailDrawer.attached')}</span>
                            ) : (
                              '—'
                            )}
                          </span>
                        </div>
                      </>
                    )}
                    {role === 'SHIPPER' && (
                      <>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelCompanyName')}</span>
                          <span className={styles.detailVal}>
                            {(profile as ShipperProfileResponse).companyName || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelGstin')}</span>
                          <span className={`${styles.detailVal} ${styles.mono}`}>
                            {(profile as ShipperProfileResponse).gstin || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelCorporatePan')}</span>
                          <span className={`${styles.detailVal} ${styles.mono}`}>
                            {(profile as ShipperProfileResponse).panNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelSecondaryPhone')}</span>
                          <span className={styles.detailVal}>
                            {(profile as ShipperProfileResponse).secondaryPhoneNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelWhatsappNumber')}</span>
                          <span className={styles.detailVal}>
                            {(profile as ShipperProfileResponse).whatsappNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelBillingAddress')}</span>
                          <span className={styles.detailVal}>
                            {(profile as ShipperProfileResponse).billingAddressText ||
                              ((profile as ShipperProfileResponse).billingAddress
                                ? `${(profile as ShipperProfileResponse).billingAddress!.line}, ${(profile as ShipperProfileResponse).billingAddress!.city}, ${(profile as ShipperProfileResponse).billingAddress!.state} - ${(profile as ShipperProfileResponse).billingAddress!.pincode}`
                                : '—')}
                          </span>
                        </div>
                      </>
                    )}
                    {role === 'FLEET_MANAGER' && (
                      <>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelCompanyName')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).companyName || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelGstin')}</span>
                          <span className={`${styles.detailVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).gstin || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelCorporatePan')}</span>
                          <span className={`${styles.detailVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).panNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelServiceRegions')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).serviceRegions || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelFleetSize')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).fleetSize || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelSecondaryPhone')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).secondaryPhoneNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelWhatsappNumber')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).whatsappNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelBillingAddress')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).billingAddress || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelBankName')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).bankName || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelAccountHolder')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).accountHolderName || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelAccountNumber')}</span>
                          <span className={`${styles.detailVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).accountNumber || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelIfscCode')}</span>
                          <span className={`${styles.detailVal} ${styles.mono}`}>
                            {(profile as FleetManagerProfileResponse).ifscCode || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelBankProofType')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).bankProofType || '—'}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelBankProof')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).bankProofUrl ? (
                              <span>{t('domain.userDetailDrawer.attached')}</span>
                            ) : (
                              '—'
                            )}
                          </span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>{t('domain.userDetailDrawer.labelPayoutVerification')}</span>
                          <span className={styles.detailVal}>
                            {(profile as FleetManagerProfileResponse).verificationStatus || '—'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={styles.emptyProfile}>{t('domain.userDetailDrawer.noSubProfile')}</div>
                )}

                {detailData.user.role !== 'ADMIN' && (
                  <Link
                    to={`/users/${detailData.user.id}`}
                    state={{ from: window.location.pathname }}
                    className={styles.fullProfileBtn}
                    onClick={onClose}
                  >
                    {t('domain.userDetailDrawer.viewFullProfile')}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isEditOpen && detailData && detailData.user.role !== 'ADMIN' && (
        <UserFormModal
          role={detailData.user.role as 'DRIVER' | 'SHIPPER' | 'FLEET_MANAGER'}
          user={detailData.user}
          initialProfile={
            detailData.profile as
              DriverProfileResponse | FleetManagerProfileResponse | ShipperProfileResponse | null
          }
          onClose={() => setIsEditOpen(false)}
        />
      )}
    </>
  );
}
